import { updateInvestorVehicleData } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import {
	useTranslationCommonForm,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	INVESTOR_BACKUP_PROPERTY,
	useBackupController,
} from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { isSameStringKeyInsensitive } from '@dealstack/shared/lib/format/string';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { useCallback, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { getMainAddress } from '@dealstack/frontend/shared/address.ts';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import {
	buildControllingPartyEntrySchema,
	fromControllingPartyResList,
	hasDuplicateControllingPartyType,
	inferSignatoryChoice,
	resolveAuthorisedSignatory,
	toControllingPartyDtos,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPIVInformation/controllingParties.utils';
import {
	SIGNATORY_CHOICE,
	type ControllingPartyEntry,
	type SavedSignatory,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPIVInformation/useJoinAndAllocationPIVInformationController.types';

export enum INVESTMENT_LEAVE_PIV_INFO_FORM {
	IS_PIV = 'isPIV',
	IS_REPRESENTATIVE_SAME_AS_INVESTOR = 'isRepresentativeSameAsInvestor',
	PIV_NAME = 'pivName',
	PIV_REG_NUMBER = 'pivRegNumber',
	PIV_COUNTRY = 'pivCountry',
	PIV_CITY = 'pivCity',
	PIV_POST_CODE = 'pivPostCode',
	PIV_STREET = 'pivStreet',
	PIV_HOUSE_NUMBER = 'pivHouseNumber',
	REPRESENTATIVE_FIRST_NAME = 'representativeFirstName',
	REPRESENTATIVE_LAST_NAME = 'representativeLastName',
	REPRESENTATIVE_EMAIL = 'representativeEmail',
	IS_CONTROLLING_PARTY_REQUIRED = 'isControllingPartyRequired',
	CONTROLLING_PARTIES = 'controllingParties',
	SIGNATORY_CHOICE = 'signatoryChoice',
	OTHER_SIGNATORY_FIRST_NAME = 'otherSignatoryFirstName',
	OTHER_SIGNATORY_LAST_NAME = 'otherSignatoryLastName',
	OTHER_SIGNATORY_EMAIL = 'otherSignatoryEmail',
	SAVED_SIGNATORY = 'savedSignatory',
}

interface PIVInfoForm {
	[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV]: boolean;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR]: boolean;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED]: boolean;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES]: ControllingPartyEntry[];
	[INVESTMENT_LEAVE_PIV_INFO_FORM.SIGNATORY_CHOICE]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: string;
	[INVESTMENT_LEAVE_PIV_INFO_FORM.SAVED_SIGNATORY]: SavedSignatory | null;
}

export const useInvestmentLeavePIVInformationController = ({
	setIsContinueDisabled,
	setIsLoading,
}: {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
}) => {
	const tInvestor = useTranslationInvestor();
	const tCommonForm = useTranslationCommonForm();
	const updateInvestorVehicleDataRequest = useWrappedRequest(updateInvestorVehicleData);
	const { dataBackup, updateBackupData, removeBackupData } = useBackupController();
	const getInvestorDataRequest = useInvestor();
	const { kycSetting } = useContext(investmentJoinAndAllocationContext);
	const queryClient = useQueryClient();

	const commonYup = useCommonYupValidation();

	const isControllingPartyRequired = kycSetting?.isControllingPartyInfoRequired ?? false;

	const requiredIfPIV = useCallback((validation = commonYup.string.required) => {
		return Yup.string().when([INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV], {
			is: true,
			then: () => validation,
		});
	}, []);

	const requiredIfRepresentativeNotSameAsInvestor = useCallback(
		(validation = commonYup.string.required) => {
			return Yup.string().when([INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV], {
				is: true,
				then: () =>
					Yup.string().when(
						[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED],
						{
							is: false,
							then: () =>
								Yup.string().when(
									[
										INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
									],
									{
										is: false,
										then: () => validation,
									}
								),
						}
					),
			});
		},
		[]
	);

	const requiredIfOtherSignatory = useCallback((schema: Yup.StringSchema) => {
		return commonYup.string.optional.when(
			[
				INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV,
				INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
				INVESTMENT_LEAVE_PIV_INFO_FORM.SIGNATORY_CHOICE,
			],
			{
				is: (isPiv: boolean, isRequired: boolean, choice: string) =>
					isPiv && isRequired && choice === SIGNATORY_CHOICE.OTHER,
				then: () => schema,
			}
		);
	}, []);

	const pivInformationForm = useForm<PIVInfoForm>({
		mode: 'onChange',
		defaultValues: {
			isPIV: false,
			isRepresentativeSameAsInvestor: true,
			isControllingPartyRequired: false,
			controllingParties: [],
			signatoryChoice: SIGNATORY_CHOICE.SELF,
			otherSignatoryFirstName: '',
			otherSignatoryLastName: '',
			otherSignatoryEmail: '',
			savedSignatory: null,
		},
		resolver: yupResolver(
			Yup.object().shape({
				[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV]: Yup.boolean(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER]: requiredIfPIV(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]:
					requiredIfRepresentativeNotSameAsInvestor(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]:
					requiredIfRepresentativeNotSameAsInvestor(),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]:
					requiredIfRepresentativeNotSameAsInvestor(commonYup.email.required),
				// Entry rules are shared with the join flow — see
				// buildControllingPartyEntrySchema (this used to be a drifted copy).
				// Gated on the programme setting AND on leaving via a vehicle: with
				// either off the section never renders (or is disabled), so
				// validating those entries would block Continue on invisible
				// fields (MIP-2667).
				[INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES]: Yup.array().when(
					[
						INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV,
						INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
					],
					{
						is: (isPiv: boolean, isRequired: boolean) => isPiv && isRequired,
						then: () =>
							Yup.array().of(
								buildControllingPartyEntrySchema(commonYup, tCommonForm)
							),
					}
				),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.SIGNATORY_CHOICE]: commonYup.string.optional.when(
					[
						INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV,
						INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
					],
					{
						is: (isPiv: boolean, isRequired: boolean) => isPiv && isRequired,
						then: () => commonYup.string.required,
					}
				),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]:
					requiredIfOtherSignatory(commonYup.string.required),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]:
					requiredIfOtherSignatory(commonYup.string.required),
				[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: requiredIfOtherSignatory(
					commonYup.email.required
				),
			})
		),
	});

	useEffect(() => {
		if (dataBackup.pivInformation) {
			pivInformationForm.reset(dataBackup.pivInformation);
			void pivInformationForm.trigger().then(() => pivInformationForm.clearErrors());
			return;
		}

		const payload = getInvestorDataRequest?.data;
		if (!payload) {
			return;
		}

		const isRepresentativeSameAsInvestor = payload.representativePerson?.email
			? isSameStringKeyInsensitive(payload.user?.email, payload.representativePerson?.email)
			: true;

		const pivMainAddress = getMainAddress(payload.pivPerson?.addresses);

		const controllingPartyEntries = fromControllingPartyResList(
			payload.controllingParties ?? []
		);
		const signatoryChoice = inferSignatoryChoice(
			controllingPartyEntries,
			payload.representativePerson?.id,
			payload.personId
		);
		const savedSignatory =
			signatoryChoice === SIGNATORY_CHOICE.EXISTING_REPRESENTATIVE
				? {
						firstName: payload.representativePerson?.firstName ?? '',
						lastName: payload.representativePerson?.lastName ?? '',
						email: payload.representativePerson?.email ?? '',
					}
				: null;

		pivInformationForm.reset({
			[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV]: !!payload.pivPerson,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME]: payload.pivPerson?.displayName,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER]:
				payload.pivPerson?.registrationDetails?.corporateRegistrationNumber,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY]: pivMainAddress?.country ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY]: pivMainAddress?.city ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE]: pivMainAddress?.postCode ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET]: pivMainAddress?.street ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER]:
				pivMainAddress?.houseNumber ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR]:
				isRepresentativeSameAsInvestor,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]:
				payload.representativePerson?.firstName ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]:
				payload.representativePerson?.lastName ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]:
				payload.representativePerson?.email ?? undefined,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED]:
				isControllingPartyRequired,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES]: controllingPartyEntries,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.SIGNATORY_CHOICE]: signatoryChoice,
			[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]: '',
			[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]: '',
			[INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: '',
			[INVESTMENT_LEAVE_PIV_INFO_FORM.SAVED_SIGNATORY]: savedSignatory,
		});
		void pivInformationForm.trigger().then(() => pivInformationForm.clearErrors());
	}, [getInvestorDataRequest?.data]);

	useEffect(() => {
		pivInformationForm.setValue(
			INVESTMENT_LEAVE_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
			isControllingPartyRequired
		);
	}, [isControllingPartyRequired]);

	const watchedControllingParties = pivInformationForm.watch(
		INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES
	);
	const hasDuplicateControllingParty = hasDuplicateControllingPartyType(
		watchedControllingParties ?? []
	);

	// Continue stays clickable while fields are invalid: the click runs
	// validation, marks every offending field red and focuses the first one.
	// Disabling on !isValid hid all of that — load-time errors are cleared, so
	// the investor faced a dead button with no visible reason (MIP-2655). Only
	// the duplicate-role case still disables, since it renders its own message.
	useEffect(() => {
		setIsContinueDisabled(hasDuplicateControllingParty);

		return () => {
			setIsContinueDisabled(false);
		};
	}, [hasDuplicateControllingParty]);

	useEffect(() => {
		void pivInformationForm.trigger(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV);
		const isPIVVisible = pivInformationForm.getValues(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV);
		if (isPIVVisible) {
			return;
		}
		//  27.09.2022 dslaby PIV is not visible - clear errors related to it
		// https://gitlab.com/d6275/dealstack/-/issues/437
		pivInformationForm.clearErrors([
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET,
			INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER,
			INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
			INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME,
			INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME,
			INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL,
		]);
	}, [pivInformationForm.watch(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV)]);

	useEffect(() => {
		const payload = getInvestorDataRequest?.data;
		const { getValues, resetField } = pivInformationForm;
		const formValues = getValues();

		if (
			!formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR] ||
			!formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV]
		) {
			return;
		}

		resetField(INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME, {
			defaultValue: payload?.user?.firstName,
		});
		resetField(INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME, {
			defaultValue: payload?.user?.lastName,
		});
		resetField(INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL, {
			defaultValue: payload?.user?.email,
		});
	}, [
		pivInformationForm.watch(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR),
		pivInformationForm.watch(INVESTMENT_LEAVE_PIV_INFO_FORM.IS_PIV),
	]);

	const onSubmit = (): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);
			const formValues = pivInformationForm.getValues();
			const payload = getInvestorDataRequest?.data;

			const legalRepresentative = isControllingPartyRequired
				? {
						...resolveAuthorisedSignatory(
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.SIGNATORY_CHOICE],
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES],
							{
								firstName:
									formValues[
										INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME
									],
								lastName:
									formValues[
										INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME
									],
								email: formValues[
									INVESTMENT_LEAVE_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL
								],
							},
							{
								firstName: payload?.user?.firstName,
								lastName: payload?.user?.lastName,
								email: payload?.user?.email,
							},
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.SAVED_SIGNATORY]
						),
						relationId: payload?.representativePerson?.relationId,
					}
				: {
						firstName:
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME],
						lastName:
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME],
						email: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.REPRESENTATIVE_EMAIL],
						relationId: payload?.representativePerson?.relationId,
					};

			const controllingParties = isControllingPartyRequired
				? toControllingPartyDtos(
						formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.CONTROLLING_PARTIES]
					)
				: undefined;

			updateInvestorVehicleDataRequest
				.fetch({
					controllingParties,
					legalRepresentative,
					personalInvestmentVehicle: {
						pivName: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_NAME],
						corporateRegistrationNumber:
							formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_REG_NUMBER],
						address: {
							country: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_COUNTRY],
							city: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_CITY],
							postCode: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_POST_CODE],
							street: formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_STREET],
							houseNumber:
								formValues[INVESTMENT_LEAVE_PIV_INFO_FORM.PIV_HOUSE_NUMBER],
						},
					},
				})
				.onAnyError(() => {
					resolve(SAVE_FAILED);
					setIsLoading(false);
				})
				.onLocalError(() => {
					showToastV2({
						title: tInvestor('investment.toast.failed_save_company_information'),
						state: 'error',
					});
				})
				.onSuccess(async () => {
					await getInvestorDataRequest.refetch();
					await queryClient.invalidateQueries({
						queryKey: [API_PATHS.SHAREHOLDER_KYC_KYC_FORMS_DOCUMENTWORKFLOW_GET],
					});
					showToastV2({ title: tInvestor('investment.toast.data_saved') });
					resolve(SAVE_OK);
					setIsLoading(false);
				});
		});

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			updateBackupData(
				INVESTOR_BACKUP_PROPERTY.pivInformation,
				pivInformationForm.getValues()
			);
			return SAVE_OK;
		}
		// Validate via handleSubmit so a failure marks every invalid field and focuses the first one.
		let isValid = false;
		await pivInformationForm.handleSubmit(async () => {
			isValid = true;
		})();
		if (!isValid) {
			return SAVE_INVALID;
		}
		const isDirty = !isEmpty(pivInformationForm.formState.dirtyFields);
		if (!isDirty && !dataBackup.pivInformation && !isControllingPartyRequired) {
			return SAVE_OK;
		}
		removeBackupData(INVESTOR_BACKUP_PROPERTY.pivInformation);
		return onSubmit();
	});

	return {
		personalInformationForm: pivInformationForm,
		onSubmit,
		isControllingPartyRequired,
		investorPersonId: getInvestorDataRequest?.data?.personId,
	};
};
